import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import http from "@/lib/api";
import { Panel, Empty, fmt } from "./AdminKit";
import ReplyPanel from "./ReplyPanel";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";

const categories = [
  { value: "general", label: "Сообщение" },
  { value: "diagnostic_result", label: "Результат диагностики" },
  { value: "repair_recommendation", label: "Рекомендация по ремонту" },
  { value: "parts_required", label: "Требуются детали" },
  { value: "cost_estimate", label: "Оценка стоимости" },
  { value: "pickup_ready", label: "Готово к выдаче" },
];

export default function AdminCommunication() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [category, setCategory] = useState("general");
  const [params] = useSearchParams();

  const loadMessages = (order) => {
    setSelected(order);
    http.get(`/v1/admin/technician-messages?order_id=${order.id}`).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  };

  useEffect(() => {
    http.get("/v1/admin/repair-orders").then((r) => {
      setOrders(r.data);
      const pre = r.data.find((o) => o.id === params.get("order"));
      if (pre) loadMessages(pre);
    }).catch(() => {});
  }, [params]);

  const send = async ({ text_ru, customer_text, send_email }) => {
    await http.post("/v1/admin/technician-messages", { order_id: selected.id, category, text: customer_text, original_ru: text_ru, send_email });
    toast.success(send_email ? "Сообщение отправлено (email + в системе)" : "Сообщение опубликовано в системе (без email)");
    loadMessages(selected);
  };

  return (
    <div className="space-y-6" data-testid="admin-communication">
      <h1 className="text-2xl font-bold text-white">Коммуникация с клиентом</h1>
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-slate-400 text-sm">
        Сообщения техника видны клиенту в системе (Vadim сообщает их после проверки личности) и, по желанию, отправляются по email. Пишите по-русски — AI подготовит текст на языке клиента и контрольный перевод. Коды, цены и модели не меняются.
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Panel title="Ремонтные заказы">
            {orders.length === 0 ? <Empty /> : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {orders.map((o) => (
                  <button key={o.id} onClick={() => loadMessages(o)} data-testid={`comm-order-${o.order_number}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm border ${selected?.id === o.id ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-700/40 border-slate-700 text-slate-200 hover:bg-slate-700"}`}>
                    <div className="font-medium">{o.order_number}</div>
                    <div className="text-xs opacity-80">{o.customer_name} · {o.machine_brand}</div>
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 text-center text-slate-500"><MessageSquare className="h-8 w-8 mx-auto mb-3" />Выберите заказ слева</div>
          ) : (
            <Panel title={`Заказ ${selected.order_number} — ${selected.customer_name}`}>
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto" data-testid="comm-history">
                {messages.length === 0 ? <Empty text="Нет сообщений" /> : messages.map((m) => (
                  <div key={m.id} className="bg-slate-700/40 border border-slate-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1"><span className="text-xs text-blue-300">{categories.find((c) => c.value === m.category)?.label || m.category}</span><span className="text-xs text-slate-500">{fmt(m.created_at)}</span></div>
                    <p className="text-sm text-slate-200 whitespace-pre-wrap">{m.text}</p>
                    {m.original_ru && <p className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">RU оригинал: {m.original_ru}</p>}
                  </div>
                ))}
              </div>
              <div className="space-y-3 border-t border-slate-700 pt-4">
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white rounded px-2 py-2 text-sm" data-testid="comm-category">
                  {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <ReplyPanel key={selected.id} id={`order-${selected.id}`} draftKey={`order-${selected.id}`} composeUrl={`/v1/admin/repair-orders/${selected.id}/compose-reply`}
                  onSend={send} emailOption={selected.email ? { default: true, label: `Также отправить на e-mail (${selected.email})` } : null} />
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
